import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import accountApi from "@/lib/accountApi";
import Dashboard from "@/pages/Dashboard";
import Editor from "@/pages/Editor";
import Login from "@/pages/Login";
import AccountLogin from "@/pages/AccountLogin";
import Admin from "@/pages/Admin";


function formatMaintenanceDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("id-ID", {
    timeZone: "Asia/Jakarta",
    dateStyle: "long",
    timeStyle: "short",
  }) + " WIB";
}

function MaintenanceScreen() {
  const [maintenance, setMaintenance] = useState({
    enabled: false,
    message: "Sistem sedang dalam pemeliharaan. Silakan coba lagi nanti.",
    starts_at: null,
    ends_at: null,
  });

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const response = await fetch("/api/maintenance", { cache: "no-store" });
        if (!response.ok) return;
        const data = await response.json();
        if (active) setMaintenance(data);
      } catch (error) {
        console.error("Gagal memeriksa Maintenance:", error);
      }
    };
    check();
    const timer = setInterval(check, 15000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center">
      <div className="max-w-lg space-y-4">
        <div className="text-6xl">🛠️</div>
        <h1 className="text-3xl font-bold">Sedang Dalam Pemeliharaan</h1>
        <p className="whitespace-pre-wrap text-muted-foreground">{maintenance.message}</p>
        {maintenance.starts_at && (
          <p className="text-sm text-muted-foreground">
            Mulai: {formatMaintenanceDate(maintenance.starts_at)}
          </p>
        )}
        {maintenance.ends_at && (
          <p className="text-sm text-muted-foreground">
            Perkiraan selesai: {formatMaintenanceDate(maintenance.ends_at)}
          </p>
        )}
        <p className="text-sm text-muted-foreground">Silakan coba kembali beberapa saat lagi.</p>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}


function MaintenanceGuard({ children }) {
  const location = useLocation();
  const [status, setStatus] = useState("checking");
  const isAdmin =
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/");
  const isMaintenancePage = location.pathname === "/maintenance";

  useEffect(() => {
    if (isAdmin) return;
    let active = true;

    const check = async () => {
      try {
        const response = await fetch("/api/maintenance", { cache: "no-store" });
        if (!response.ok) {
          if (active) setStatus("available");
          return;
        }
        const data = await response.json();
        if (active) setStatus(data.enabled ? "maintenance" : "available");
      } catch (error) {
        console.error("Gagal memeriksa status Maintenance:", error);
        if (active) setStatus("available");
      }
    };

    setStatus("checking");
    check();
    const timer = setInterval(check, 15000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [isAdmin]);

  if (isAdmin) return children;
  if (status === "checking") return <LoadingScreen />;
  if (status === "maintenance" && !isMaintenancePage) {
    return <Navigate to="/maintenance" replace />;
  }
  if (status === "available" && isMaintenancePage) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function RequireAccount({ children }) {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("pp_account_token");

    if (!token) {
      setStatus("missing");
      return () => {
        active = false;
      };
    }

    accountApi
      .get("/account/me")
      .then(() => {
        if (active) setStatus("valid");
      })
      .catch(() => {
        localStorage.removeItem("pp_account_token");
        if (active) setStatus("missing");
      });

    return () => {
      active = false;
    };
  }, []);

  if (status === "checking") return <LoadingScreen />;
  if (status === "missing") return <AccountLogin />;
  return children;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MaintenanceGuard>
          <Routes>
            <Route path="/account" element={<AccountLogin />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/maintenance" element={<MaintenanceScreen />} />
            <Route
              path="/"
              element={
                <RequireAccount>
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                </RequireAccount>
              }
            />
            <Route
              path="/editor/:id"
              element={
                <RequireAccount>
                  <RequireAuth>
                    <Editor />
                  </RequireAuth>
                </RequireAccount>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </MaintenanceGuard>
          <Toaster position="top-center" richColors visibleToasts={1} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
